import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link, useInRouterContext, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOptionalAuth } from '@/contexts/AuthContext';
import { homeFor } from '@/lib/home-route';
import { homeLabel as navHomeLabel } from '@/lib/parentFor';
import type { User } from '@/types/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. When omitted, the default page-error card renders. */
  fallback?: ReactNode;
  /**
   * Where the "Back" affordance links. Default: the reader's home,
   * `homeFor(user.role)` (design grammar §4.6, recovery pages). It used to
   * be `/trfer` for every role, so a parent or an admin was sent to a page
   * that bounced them.
   */
  homeHref?: string;
  /** The affordance's whole text. Default: "Back to {home's nav label}". */
  homeLabel?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Generic route-level error boundary. Catches render-time throws in its subtree
 * and shows a friendly fallback instead of a white screen, so one bad record or
 * a downstream component error can't blank the whole page. Reset re-mounts the
 * subtree; the Back affordance routes the user somewhere safe.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface it — a boundary should not swallow the error silently.
    console.error('[ErrorBoundary] caught render error:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <ErrorBoundaryCard
        homeHref={this.props.homeHref}
        homeLabel={this.props.homeLabel}
        error={this.state.error}
        onRetry={this.handleRetry}
      />
    );
  }
}

/**
 * The default page-error card. A function component so its words come from
 * the `feedback` namespace in the reader’s language (#2109 R3 L8); the class
 * above cannot call a hook.
 */
function ErrorBoundaryCard({
  homeHref,
  homeLabel,
  error,
  onRetry,
}: {
  homeHref?: string;
  homeLabel?: string;
  error: Error | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation(['feedback', 'common', 'shell']);
  // Optional: the boundary also wraps trees with no AuthProvider (tests), and
  // there the home is the role-less `homeFor(undefined)`.
  const role = useOptionalAuth()?.user?.role as User['role'] | undefined;
  const inRouter = useInRouterContext();
  const href = homeHref ?? homeFor(role);
  const navLabel = navHomeLabel(href);
  const backText = homeLabel ?? (navLabel ? t('shell:header.backTo', { label: navLabel }) : t('shell:notFound.backHome'));
  const backContent = (
    <>
      <ArrowLeft className="h-4 w-4" />
      {backText}
    </>
  );
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-8">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center space-y-4">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
          <div>
            <h3 className="text-lg font-semibold">{t('errorBoundary.title')}</h3>
            <p className="text-body text-stevens-ink-3 mt-1">
              {t('errorBoundary.body')}
            </p>
          </div>
          {import.meta.env.DEV && error && (
            <pre className="text-caption text-left bg-muted p-3 rounded-md overflow-auto max-h-32">
              {error.message}
            </pre>
          )}
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" asChild className="gap-2">
              {/* A router Link inside the app (grammar §4.6: a destination is
                  a Link); a plain anchor only where no router exists above
                  the boundary, which a Link cannot render without. */}
              {inRouter ? <RouterBackLink href={href}>{backContent}</RouterBackLink> : <a href={href}>{backContent}</a>}
            </Button>
            <Button onClick={onRetry} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              {t('common:actions.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The card's back control inside a router. When the page that crashed IS the
 * target (a reader's home page threw), a client-side Link to the same path
 * would keep the pathname-keyed boundary mounted and the card on screen, so
 * that one case reloads the document, as the plain anchor always did.
 */
function RouterBackLink({ href, children }: { href: string; children: ReactNode }) {
  const { pathname } = useLocation();
  const samePage = href.split(/[?#]/)[0].replace(/\/+$/, '') === pathname.replace(/\/+$/, '');
  return <Link to={href} reloadDocument={samePage}>{children}</Link>;
}

/**
 * Router-aware boundary for wrapping a layout's `<Outlet />`. Keyed by
 * pathname so it resets on navigation — a render error on one route shows the
 * fallback for that route only, and moving to another route mounts a fresh
 * boundary instead of carrying a stale error state. Keeps the surrounding
 * layout chrome (nav) mounted.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}
